import {
  ApiError,
  GamePass,
  LinkedAccountType,
  NotificationType,
  Offer,
  RemoteNotificationTrigger,
} from "@blacknut/javascript-sdk/dist";
import styles from "./styles.module.scss";
import { getGamePass } from "@blacknut/javascript-sdk/dist/api/BlacknutPlus/getGamePass";
import { logD, logE } from "@blacknut/logging/dist";
import { MessageModal, ModalSubscription, useModal } from "@blacknut/react-sdk";
import {
  addNotification,
  AppLayout,
  getLinkedAccounts,
  NotificationMessage,
  NotificationMessageKind,
  notificationService,
  State,
  useAnalytics,
  useLayout,
  useProfiles,
  userUpdate,
} from "core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { firstValueFrom } from "rxjs";
import useSubscribed from "src/hooks/useSubscribed";
import { LOGGING_TAG } from "../Utils";
import { useErrorHandling } from "../V2ErrorHandler";
import { useWarioURL } from "../useWarioURL";
import React from "react";
import { useAppNavigation } from "../Navigation/useAppNavigation";
import { useLocation } from "react-router-dom-v5-compat";
import { useTheme } from "src/theme/ThemeProvider";
import { LinkAccountModal } from "src/components/Modals/LinkAccountModal";
import { QRCodeModal } from "src/components/Modals/QRCodeModal";
import { isTryBuy } from "../subscription";
import { HeaderElementModal } from "src/components/Modals/HeaderElementModal";

interface PassTVModalProps {
  gamePass: GamePass;
  url: string;
  onClose: () => void;
  isOpen: boolean;
  title?: string;
  description?: string;
  onSubscribe?: () => void;
}

export const PassModal = (props: PassTVModalProps) => {
  const { gamePass, url, onClose, title, description, onSubscribe } = props;
  const { t } = useTranslation();
  const { theme } = useTheme();
  const layout = useLayout();
  const background = gamePass.images.find((im) => im.kind === "background");
  const logo = gamePass.images.find((im) => im.kind === "logo");
  if (layout === AppLayout.TV) {
    return (
      <QRCodeModal
        {...props}
        title={title ? title : gamePass.pre_subscription.title}
        description={description ? description : gamePass.pre_subscription.description}
        btnText={t("dialogs.unlockPass.no")}
        URL={url}
        onClose={onClose}
        theme={theme}
        headerElement={
          <div className={styles.background}>
            <img className={styles.logo} src={logo?.url} />
            <img className={styles.background} src={background?.url} />
          </div>
        }
      />
    );
  }
  return (
    <HeaderElementModal
      {...props}
      title={title ? title : gamePass.pre_subscription.title}
      description={description ? description : gamePass.pre_subscription.description}
      onClose={onClose}
      positiveButtonText={t("buttons.confirm")}
      negativeButtonText={t("buttons.cancel")}
      onPressPositive={onSubscribe}
      onPressNegative={onClose}
      headerElement={
        <div className={styles.background}>
          <img className={styles.logo} src={logo?.url} />
          <img className={styles.background} src={background?.url} />
        </div>
      }
    />
  );
};

export const useGamePassUnlock = ({
  ubisoftSSORequired = false,
  passId,
}: {
  ubisoftSSORequired?: boolean;
  passId?: string;
}) => {
  const { profile, familyProfiles } = useSelector((state: State) => state.profilesState);
  const layout = useLayout();
  const { push: modalPush } = useModal();
  const modalSubscription = useRef<ModalSubscription>();
  const { t } = useTranslation();
  const location = useLocation();
  const { create: openWario } = useWarioURL();
  const pathname = location.pathname;

  const { onSelectProfile } = useProfiles();
  const { trackPageView, trackPass } = useAnalytics();
  const dispatch = useDispatch();

  const { user } = useSelector((state: State) => state.globalState);
  const { billingInfo } = useSelector((state: State) => state.globalState);
  const _isTryBuy = isTryBuy(billingInfo.subscription);

  const { navigate } = useAppNavigation();
  const [error, setError] = useState<ApiError>();
  useErrorHandling({
    error,
    clearError: () => {
      setError(undefined);
    },
  });
  const closeModal = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
  }, []);
  const masterProfile = familyProfiles.profiles?.find((p) => p.isMaster);

  const { checkForValidSubscription } = useSubscribed(passId);

  useEffect(() => {
    const sub = notificationService.onMessage().subscribe((n: NotificationMessage) => {
      if (n.kind !== NotificationMessageKind.NOTIFICATION_RESP) return;
      const { notification } = n.payload as any;

      if (notification.trigger === RemoteNotificationTrigger.PLAN_SUBSCRIBED) {
        logD(LOGGING_TAG, "Received plan subscribed notification: %o", notification);
        user && dispatch(userUpdate(user));
        addNotification({
          type: NotificationType.SUCCESS,
          time: new Date().getTime(),
          message: "notifications.subscriptionupdated",
        })(dispatch);
        closeModal();
      }
      if (notification.trigger === RemoteNotificationTrigger.UBISOFT_CONNECT) {
        logD(LOGGING_TAG, "Received ubisoft connect notification: %o", notification);
        if (notification.status === "success") {
          user && dispatch(userUpdate(user));
          addNotification(
            {
              type: NotificationType.SUCCESS,
              time: new Date().getTime(),
              message: "notifications.ubisoftconnect.success",
            },
            8000,
          )(dispatch);
          closeModal();
        }
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [closeModal, dispatch, navigate, user]);

  const unlockPass = useCallback(
    async (gameId?: string) => {
      passId && trackPass(passId, gameId);

      if (!profile?.isMaster) {
        logD(LOGGING_TAG, "Profile is not master");

        const redirectUrl = pathname;
        modalSubscription.current = modalPush((props) => (
          <MessageModal
            {...props}
            title={t("dialogs.unlockPass.title")}
            message={t("dialogs.unlockPass.message")}
            onPressPositive={async () => {
              if (masterProfile) {
                await onSelectProfile(masterProfile, redirectUrl);
              }
            }}
            onPressNegative={closeModal}
            onClose={closeModal}
            positiveButtonText={t("buttons.switchUser")}
            negativeButtonText={t("buttons.cancel")}
          />
        ));
        return;
      }

      const gamePass = passId ? await firstValueFrom(getGamePass(passId)) : undefined;

      if (!checkForValidSubscription(gamePass && gamePass)) {
        return;
      }

      if (_isTryBuy || !gamePass) return;

      const getWarioURL = async (offer: Offer) => {
        if (layout === AppLayout.TV) {
          trackPageView("Unlock_pass_tv");
          const path = `dashboard/subscriptions/pass/${offer.uuid}/checkout?device=tv`;
          return await openWario(path, undefined, { tokens: false });
        }
        const path = `dashboard/subscriptions/pass/${offer.uuid}/checkout`;
        return await openWario(path, {
          returnUrl: window.location.toString(),
        });
      };

      logD(LOGGING_TAG, "Profile is master");
      try {
        const warioURL = await getWarioURL(gamePass.offer);

        if (ubisoftSSORequired) {
          const linkedAccounts = await getLinkedAccounts()(dispatch);
          const isUbisoftLinked = linkedAccounts.some(
            (account) => account.kind === LinkedAccountType.UBISOFT,
          );
          if (!isUbisoftLinked) {
            if (layout !== AppLayout.TV) {
              window.open(warioURL.toString(), "_blank");
            }
            const background = gamePass.images.find((im) => im.kind === "background");
            const logo = gamePass.images.find((im) => im.kind === "logo");
            const brandedHeader = (
              <div className={styles.background}>
                <img className={styles.logo} src={logo?.url} />
                <img className={styles.background} src={background?.url} />
              </div>
            );

            modalSubscription.current = modalPush((props) => (
              <LinkAccountModal
                {...props}
                url={`${warioURL}`}
                onClose={closeModal}
                headerElement={layout === AppLayout.TV ? brandedHeader : <></>}
              />
            ));
            return;
          }
        }

        if (layout === AppLayout.TV) {
          modalSubscription.current = modalPush((props) => (
            <PassModal
              {...props}
              onClose={closeModal}
              url={warioURL.toString()}
              gamePass={gamePass}
              title={t("dialogs.pass.subscribe.title", { passName: gamePass.name })}
              description={t("dialogs.pass.tvSubscribe.message")}
            />
          ));
        } else {
          window.open(warioURL, "_self");
        }
      } catch (e) {
        logE(LOGGING_TAG, "Error BN+ get pass", e);
        setError(e as ApiError);
      }
    },
    [
      _isTryBuy,
      checkForValidSubscription,
      closeModal,
      dispatch,
      layout,
      masterProfile,
      modalPush,
      onSelectProfile,
      openWario,
      passId,
      pathname,
      profile?.isMaster,
      t,
      trackPageView,
      trackPass,
      ubisoftSSORequired,
    ],
  );
  return { unlockPass };
};

export default useGamePassUnlock;
